import React, { useState, useEffect, useContext } from 'react';
import Modal from 'react-modal';
import { BeatLoader } from 'react-spinners';
import './ActivateZone.css';
import Toastify from "toastify-js";
import Web3Context from '../../../context/Web3Context';
import UserAuthContext from '../../../context/UserAuthContext';
import { getContract } from '../../../Web3Utils'; // Import Web3Utils functions
import { getBNBPriceInUSD } from '../coingeckoAPI';
import SmartRainmakersABI from '../../../NewTokenABI.json'; // Import ABI
import CongratulationsModal from '../../CongratulationsModal'; // Import CongratulationsModal
import ENDPOINTS, { RPC_BASE_URL, USDT_CONTRACT_ADDRESS } from '../../../endpoints';
import Web3 from 'web3';
import { convertUsdToBnb } from '../../../USDToBNBConversion';
import axios from 'axios';

interface ActivateZoneProps {
  isOpen: boolean;
  onRequestClose: () => void;
  count: number;
  isRainmaker: boolean;
  zone: string;
  currentLevel: string;
  nextLevel: string;
  onConfirmActivation: () => void;
}

interface OperationPayload {
  user_id: string;
  rainmakerAmount: number;
  premiumAmount: number;
  referrer_id: string;
  type: 'BNB' | 'USDT';
  zone: string;
}

Modal.setAppElement('#root');

const ActivateZone: React.FC<ActivateZoneProps> = ({ isOpen, onRequestClose, count, isRainmaker, zone, onConfirmActivation, nextLevel, currentLevel }) => {
  const { web3, account, initWeb3Manually } = useContext(Web3Context); // Use Web3Context for web3 and account
  const { userData } = useContext(UserAuthContext); // Use UserAuthContext for user data
  const [bnbPrice, setBnbPrice] = useState<number | null>(null);
  const [bnbEquivalent, setBnbEquivalent] = useState<number | null>(null);
  const [yourBalance, setYourBalance] = useState<number | null>(null);
  const [yourBNBBalance, setYourBNBBalance] = useState<number | null>(null);
  const [gasFee, setGasFee] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCongratulationsModalOpen, setIsCongratulationsModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(false);
  const [yourUSDTBalance, setYourUSDTBalance] = useState<number | null>(null);
  // New state for selected currency
  const [selectedCurrency, setSelectedCurrency] = useState<'BNB' | 'USDT'>('BNB');

  useEffect(() => {
    if (isOpen) {
      setTimeLeft(500); // Reset timer when modal opens
    }
  }, [isOpen]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (isOpen) {
      onRequestClose();
      Toastify({
        text: "You are timed out.",
        duration: 3000,
        close: true,
        gravity: "top",
        position: "center",
        style: {
          background: "linear-gradient(to right, #FF0000, #CB4335)",
        },
      }).showToast();
    }
  }, [timeLeft, isOpen, onRequestClose]);

  useEffect(() => {
    const fetchBNBPrice = async () => {
      try {
        const price = await getBNBPriceInUSD();
        console.log("Fetched BNB price:", price);
        setBnbPrice(price);
      } catch (error) {
        console.error("Error fetching BNB price:", error);
      }
    };

    fetchBNBPrice();
  }, []);

  useEffect(() => {
    if (bnbPrice) {
      const fetchBalance = async () => {
        try {
          const web3 = new Web3(Web3.givenProvider || RPC_BASE_URL);

          const currentBalance = await web3.eth.getBalance(userData!.wallet_address);
          const balanceInBNB = Number(web3.utils.fromWei(currentBalance.toString(), 'ether'));
          const bnbValue = count / bnbPrice;
          setBnbEquivalent(bnbValue);
          const balanceInUSD = balanceInBNB * bnbPrice;
          setYourBalance(balanceInUSD);
          setYourBNBBalance(balanceInBNB);

          const gasPrice = await web3.eth.getGasPrice(); // Get gas price in Wei
          const gasLimit = 21000; // Standard gas limit for a simple transfer

          // Convert gas price from Wei to BNB
          const gasPriceInBNB = Number(web3.utils.fromWei(gasPrice.toString(), 'ether'));

          // Calculate total gas fee (gasPrice * gasLimit)
          const gasFeeInBNB = gasPriceInBNB * gasLimit;

          // Convert to USD
          const gasFeeInUSD = gasFeeInBNB * bnbPrice;
          setGasFee(gasFeeInUSD);

          console.log("Gas Price (Wei):", gasPrice);
          console.log("Gas Price (BNB):", gasPriceInBNB);
          console.log("Total Gas Fee (BNB):", gasFeeInBNB);
          console.log("Total Gas Fee (USD):", gasFeeInUSD);

          // Fetch USDT balance
          const usdtAddress = USDT_CONTRACT_ADDRESS; // Replace with actual USDT contract address
          const usdtAbi = [ // Simplified ERC20 ABI for balanceOf and decimals
            {
              constant: true,
              inputs: [{ name: "_owner", type: "address" }],
              name: "balanceOf",
              outputs: [{ name: "balance", type: "uint256" }],
              type: "function"
            },
            {
              constant: true,
              inputs: [],
              name: "decimals",
              outputs: [{ name: "", type: "uint8" }],
              type: "function"
            }
          ];
          const usdtContract = new web3.eth.Contract(usdtAbi, usdtAddress);
          const usdtRawBalance = await usdtContract.methods.balanceOf(userData!.wallet_address).call();
          const usdtDecimals = Number(await usdtContract.methods.decimals().call());
          const usdtBalance = Number(usdtRawBalance) / Math.pow(10, usdtDecimals);
          setYourUSDTBalance(usdtBalance);
        } catch (error) {
          console.error("Error fetching balance:", error);
        }
      };

      fetchBalance(); // Call the async function inside useEffect
    }
  }, [bnbPrice, count, userData]);

  useEffect(() => {
    const checkUplineStatus = async () => {
      if (isOpen) {
        setLoading(true);
        setWarning('');

        try {
          const token = localStorage.getItem('token');

          const res = await axios.post(ENDPOINTS.checkUplineStatus, {
            user_id: userData?.id,
            level: nextLevel,
            current_level: currentLevel,
            zone: zone,
          }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (res.data?.uplineExists === false) {
            setWarning('Your upline is not activated yet.');
          }
        } catch (error) {
          console.error('Error checking upline status:', error);
          setWarning('Failed to check upline status.');
        } finally {
          setLoading(false);
        }
      }
    };

    checkUplineStatus();
  }, [isOpen]);

  const handleActivation = async () => {

    console.log(yourUSDTBalance);
    console.log(yourBalance);
    console.log(count);
    console.log(selectedCurrency);
    if (selectedCurrency === "BNB") {
      if (count > (yourBalance ?? 0)) {
        Toastify({
          text: `${selectedCurrency} price is less than activation amount. Please deposit funds.`,
          duration: 3000,
          gravity: "top",
          position: "center",
          style: { background: "linear-gradient(to right, #FF0000, #CB4335)" },
        }).showToast();
        return;
      }
    } else if (selectedCurrency === "USDT") {
      if (count > (yourUSDTBalance ?? 0)) {
        Toastify({
          text: `${selectedCurrency} price is less than activation amount. Please deposit funds.`,
          duration: 3000,
          gravity: "top",
          position: "center",
          style: { background: "linear-gradient(to right, #FF0000, #CB4335)" },
        }).showToast();
        return;
      }
    }

    if (!bnbPrice || isNaN(bnbPrice)) {
      Toastify({
        text: "BNB price is invalid. Please try again later.",
        duration: 3000,
        gravity: "top",
        position: "center",
        style: { background: "linear-gradient(to right, #FF0000, #CB4335)" },
      }).showToast();
      return;
    }

    showProcessingToast("Processing Activation");

    setIsLoading(true);

    try {
      if (!userData) {
        console.error("User data is missing!");
        showErrorToast("User data not found. Please try again.");
        setIsLoading(false);
        return;
      }
      console.log("User Data testing:", userData);
      const payload: OperationPayload = zone === "both" ?
        {
          user_id: userData.id,
          rainmakerAmount: count / 2,
          premiumAmount: count / 2,
          referrer_id: userData.sponsor_id!,
          type: selectedCurrency,
          zone: zone
        }
        :
        isRainmaker ? {
          user_id: userData.id,
          rainmakerAmount: count,
          premiumAmount: 0.00,
          referrer_id: userData.sponsor_id!,
          type: selectedCurrency,
          zone: zone
        }
          :
          {
            user_id: userData.id,
            rainmakerAmount: 0.00,
            premiumAmount: count,
            referrer_id: userData.sponsor_id!,
            type: selectedCurrency,
            zone: zone
          };

      // Call the operation API
      await callOperationAPI(payload);

    } catch (error) {
      console.error("Error during activation:", error);
      Toastify({
        text: "An error occurred during activation. Please try again.",
        duration: 3000,
        gravity: "top",
        position: "center",
        style: { background: "linear-gradient(to right, #FF0000, #CB4335)" },
      }).showToast();
    } finally {
      setIsLoading(false);
    }
  };

  // Function to call the operation API
  const callOperationAPI = async (payload: OperationPayload): Promise<void> => {
    try {
      console.log("Sending data to operationAPI:", payload);
      const token = localStorage.getItem('token');

      const response = await axios.post(ENDPOINTS.operation,
        JSON.stringify(payload),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        });

      const data = response.data;
      console.log("Printing OperationAPI DATA", data);

      if (data.status === 'success') {
        setIsCongratulationsModalOpen(true);
        handleCloseCongratulationsModal();
        showProcessingToast("Activation Completed Successfully");
        console.log('Operation completed successfully:', data);
      } else {
        showErrorToast(data.message);
        console.error('Operation failed:', data.error || 'Unknown error');
      }
    } catch (error: any) {
      console.error("Operation API call failed:", error);

      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Server responded with a status outside 2xx
          console.error("Backend responded with error:", error.response.data);
          showErrorToast(error.response.data.message || 'Server error occurred.');
        } else if (error.request) {
          // Request made but no response received
          console.error("No response received from server:", error.request);
          showErrorToast("No response from server. Please check your connection.");
        } else {
          // Something happened in setting up the request
          console.error("Axios setup error:", error.message);
          showErrorToast(error.message);
        }
      } else {
        // Not an Axios error
        showErrorToast("Unexpected error. Please try again.");
      }
    }
  };

  const showProcessingToast = (message: string) => {
    Toastify({
      text: message,
      duration: 5000,
      gravity: 'top',
      position: 'center',
      backgroundColor: "linear-gradient(to right, #00b09b, #96c93d)",
    }).showToast();
  };

  const showErrorToast = (message: string) => {
    Toastify({
      text: message ?? "Something went wrong!",
      duration: 5000,
      gravity: 'top',
      position: 'center',
      backgroundColor: "linear-gradient(to right, #FF5F6D, #FFC371)",
    }).showToast();
  };

  const handleCloseCongratulationsModal = () => {
    setTimeout(() => {
      setIsCongratulationsModalOpen(false);
      window.location.reload();
      onConfirmActivation();
    }, 5000);
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        contentLabel="Activate Zone Modal"
        className="activation-modal"
      >
        <h2>Activate Zone</h2>
        <div className="modal-content">
          {loading && <p>Checking upline status...</p>}

          {warning && (
            <div className="warning-message">
              {warning}
            </div>
          )}
          {/* Currency selector */}
          <div className="currency-selector">
            <label htmlFor="currency-select">Select Currency: </label>
            <select
              id="currency-select"
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value as 'BNB' | 'USDT')}
            >
              <option value="BNB">BNB</option>
              <option value="USDT">USDT</option>
            </select>
          </div>
          <table>
            <tbody>
              <tr>
                <td>Activation Fee</td>
                <td>${count} {bnbPrice && `(${bnbEquivalent?.toFixed(4)} BNB)`}</td>
              </tr>
              {selectedCurrency === 'BNB' ? (
                <tr>
                  <td>Your Balance</td>
                  <td>${(yourBalance ?? 0).toFixed(2)} ({(yourBNBBalance ?? 0).toFixed(4)} BNB)</td>
                </tr>
              ) : (
                <tr>
                  <td>Your Balance</td>
                  <td>${yourUSDTBalance?.toFixed(2) ?? '0.00'} (USDT)</td>
                </tr>
              )}
              {gasFee && (
                <tr>
                  <td>Gas Fee</td>
                  <td>{gasFee?.toFixed(4)} $ appx.</td>
                </tr>
              )}
            </tbody>
          </table>

          <button
            onClick={handleActivation}
            className="confirm-button"
            disabled={isLoading}
          >
            {isLoading ? <BeatLoader size={8} color={"#ffffff"} /> : 'Confirm Activation'}
          </button>
        </div>
      </Modal>

      <CongratulationsModal
        isOpen={isCongratulationsModalOpen}
        onRequestClose={handleCloseCongratulationsModal}
      />
    </>
  );
};

export default ActivateZone;